import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';

interface User {
  id: string;
  email: string;
  name: string;
  picture: string;
  domain: string;
  loginTime: number;
  expiresAt: number;
  first_name?: string;
  last_name?: string;
  salesforce_role?: string;
  is_onboarded?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credential: string) => void;
  logout: () => void;
  updateUserProfile: (profileData: { first_name: string; last_name: string; salesforce_role: string }) => Promise<void>;
  checkDomainRestriction: (email: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('AuthContext: Checking authentication state');
    // Check if user is already logged in from localStorage
    const savedUser = localStorage.getItem('sfdh_user');
    console.log('AuthContext: Saved user from localStorage:', savedUser ? 'Found' : 'Not found');
    
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        
        // Check if the user session has expired
        if (userData.expiresAt && Date.now() > userData.expiresAt) {
          console.log('User session expired, logging out');
          localStorage.removeItem('sfdh_user');
          setUser(null);
        } else {
          console.log('AuthContext: User authenticated from localStorage');
          setUser(userData);
        }
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('sfdh_user');
      }
    }
    console.log('AuthContext: Setting loading to false');
    setIsLoading(false);
  }, []);

  const checkDomainRestriction = (email: string): boolean => {
    const domain = email.split('@')[1];
    return domain === 'salesforce.com';
  };

  const login = async (credential: string) => {
    try {
      const decoded: { sub: string; email: string; name: string; picture: string } = jwtDecode(credential);
      
      // Check if the email domain is @salesforce.com
      if (!checkDomainRestriction(decoded.email)) {
        throw new Error('Only @salesforce.com email addresses are allowed to access this application.');
      }

      const now = Date.now();
      const loginTime = now;
      const expiresAt = now + (14 * 24 * 60 * 60 * 1000); // 14 days from now

      // Create initial user data
      const initialUserData: User = {
        id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        domain: decoded.email.split('@')[1],
        loginTime,
        expiresAt
      };

      // Try to fetch existing user profile from backend
      try {
        const response = await fetch(`/api/users/profile/${decoded.sub}`);
        if (response.ok) {
          const profileData = await response.json();
          const userData: User = {
            ...initialUserData,
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            salesforce_role: profileData.salesforce_role,
            is_onboarded: profileData.is_onboarded
          };
          setUser(userData);
          localStorage.setItem('sfdh_user', JSON.stringify(userData));
        } else {
          // User doesn't exist yet, use initial data
          setUser(initialUserData);
          localStorage.setItem('sfdh_user', JSON.stringify(initialUserData));
        }
      } catch (error) {
        console.warn('Could not fetch user profile, using initial data:', error);
        // Fallback to initial data if profile fetch fails
        setUser(initialUserData);
        localStorage.setItem('sfdh_user', JSON.stringify(initialUserData));
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('sfdh_user');
  };

  const updateUserProfile = async (profileData: { first_name: string; last_name: string; salesforce_role: string }) => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    try {
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          email: user.email,
          ...profileData
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedUserData = await response.json();
      const updatedUser = { ...user, ...updatedUserData };
      setUser(updatedUser);
      localStorage.setItem('sfdh_user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUserProfile,
    checkDomainRestriction
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
